import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import {
  acknowledgePersistedFieldValue,
  findIntakeItemForField,
  hydrateFieldStates,
  type IntakeFieldStates,
  updateFieldStatesForDraftChange,
} from '../../fieldPersistence';
import { useUpdateAdminIntakeHubItemEvidenceMutation } from '../../store/api';
import type {
  GeneratedIntakeField,
  GeneratedIntakeItem,
  IntakeFieldValue,
  IntakeHubAggregateProviderResponse,
  IntakeHubAggregateResponse,
} from '../../types';

import { IntakeFieldsPage } from './IntakeFieldsPage';
import { RAIL_SECTIONS } from './navigation';

interface AggregateIntakePageProps {
  aggregate?: IntakeHubAggregateResponse;
  currentPageKey: string;
  error?: unknown;
  isFetching?: boolean;
  onPageChange: (page: number) => void;
  organizationId: string;
  onRetry?: () => void;
}

interface AggregateFieldGroupProps {
  catalogVersion: string;
  fields: GeneratedIntakeField[];
  items: GeneratedIntakeItem[];
  organizationId: string;
  title: string;
  userId: string | null;
}

const SUMMARY_PAGES = new Set(['F1', 'M5', 'M6', 'P6', 'X1']);
const DOCUMENT_PAGES = new Set(['M1', 'P1']);

function pageLabel(pageKey: string): string {
  return (
    RAIL_SECTIONS.flatMap(section => section.items).find(item => item.pageKey === pageKey)?.label ??
    pageKey
  );
}

function AggregateFieldGroup({
  catalogVersion,
  fields,
  items,
  organizationId,
  title,
  userId,
}: AggregateFieldGroupProps) {
  const [fieldStates, setFieldStates] = useState<IntakeFieldStates>({});
  const [updateEvidence] = useUpdateAdminIntakeHubItemEvidenceMutation();

  useEffect(() => {
    setFieldStates(previous => hydrateFieldStates(fields, items, previous, userId));
  }, [fields, items, userId]);

  const draftValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [fieldId, state.draftValue]),
      ),
    [fieldStates],
  );
  const acknowledgedValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [fieldId, state.acknowledgedValue]),
      ),
    [fieldStates],
  );
  const hasAcknowledgedValues = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(fieldStates).map(([fieldId, state]) => [
          fieldId,
          state.hasAcknowledgedValue,
        ]),
      ),
    [fieldStates],
  );
  const disabledReasons = useMemo(() => {
    const reasons: Record<string, string> = {};
    for (const field of fields) {
      if (field.is_writable === false) continue;
      const item = findIntakeItemForField(field, items, userId);
      if (field.destination.kind === 'intake_item_sensitive') {
        const onFile = item?.sensitive_fields_on_file?.includes(field.destination.field_key);
        reasons[field.field_id] = onFile
          ? 'Secure value is on file; staff cannot view or replace it.'
          : 'Secure value is not on file; the provider must complete it through the secure flow.';
      } else if (field.destination.kind !== 'intake_item_evidence') {
        reasons[field.field_id] =
          field.destination.kind === 'document_reference'
            ? 'Select this provider before managing document ownership.'
            : 'Complete this value in its dedicated Intake Hub section.';
      } else if (!item?.intake_item_id) {
        reasons[field.field_id] = 'This authorized field is currently unavailable.';
      }
    }
    return reasons;
  }, [fields, items, userId]);

  const handleDraftChange = useCallback(
    (field: GeneratedIntakeField, value: IntakeFieldValue) => {
      setFieldStates(previous => updateFieldStatesForDraftChange(fields, previous, field, value));
    },
    [fields],
  );
  const handlePersistField = useCallback(
    async (field: GeneratedIntakeField, value: IntakeFieldValue) => {
      if (field.destination.kind !== 'intake_item_evidence') {
        throw new Error('This aggregate field cannot be edited by staff');
      }
      const item = findIntakeItemForField(field, items, userId);
      if (!item?.intake_item_id) throw new Error('Intake field destination is unavailable');
      await updateEvidence({
        organizationId,
        ...(userId ? { userId } : {}),
        intakeItemId: item.intake_item_id,
        updateItemEvidenceRequest: {
          catalog_version: catalogVersion,
          extracted_fields: { [field.destination.field_key]: value },
        },
      }).unwrap();
      setFieldStates(previous => acknowledgePersistedFieldValue(previous, field.field_id, value));
    },
    [catalogVersion, items, organizationId, updateEvidence, userId],
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <IntakeFieldsPage
          title={title}
          fields={fields}
          draftValues={draftValues}
          acknowledgedValues={acknowledgedValues}
          hasAcknowledgedValues={hasAcknowledgedValues}
          onDraftChange={handleDraftChange}
          onPersistField={handlePersistField}
          disabledReasons={disabledReasons}
          onContinue={() => undefined}
          nextLabel=""
          embedded
        />
      </CardContent>
    </Card>
  );
}

function ProviderSummary({ provider }: { provider: IntakeHubAggregateProviderResponse }) {
  // journey_status ships with the aggregate payload; read it defensively so a
  // not-yet-regenerated API client still compiles.
  const verifiedAt = (provider as { journey_status?: { verified_at?: string | null } })
    .journey_status?.verified_at;
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack
          direction="row"
          sx={{ alignItems: 'center', gap: 2, justifyContent: 'space-between' }}
        >
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {provider.display_name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Provider ID {provider.user_id}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            {verifiedAt && <Chip label="Verified" color="success" size="small" />}
            <Chip label={`${provider.progress.percent_complete}% complete`} size="small" />
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {provider.progress.complete_count} complete · {provider.progress.missing_count} missing ·{' '}
          {provider.progress.needs_attention_count} need attention
        </Typography>
      </CardContent>
    </Card>
  );
}

export function AggregateIntakePage({
  aggregate,
  currentPageKey,
  error,
  isFetching = false,
  onPageChange,
  organizationId,
  onRetry,
}: AggregateIntakePageProps) {
  if (isFetching && !aggregate) return <LinearProgress aria-label="Loading all providers intake" />;
  if (error && !aggregate) {
    return (
      <Alert
        severity="error"
        action={
          onRetry ? (
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          ) : undefined
        }
      >
        The all-providers intake could not be loaded. No changes have been made.
      </Alert>
    );
  }
  if (!aggregate || (aggregate.total ?? aggregate.providers?.length ?? 0) === 0) {
    return (
      <Alert severity="info">
        This organization has no active rostered providers in the current tenant.
      </Alert>
    );
  }

  const providers = aggregate.providers ?? [];
  const successfulProviders = providers.filter(provider => provider.form && !provider.error);
  const currentCanvases = successfulProviders.flatMap(provider =>
    (provider.form?.canvases ?? [])
      .filter(canvas => canvas.canvas_key === currentPageKey && canvas.is_active)
      .map(canvas => ({ canvas, provider })),
  );
  const organizationRequirementKeys = new Set(
    (aggregate.organization_items ?? []).map(item => item.requirement_key),
  );
  const organizationFields = [
    ...new Map(
      currentCanvases
        .flatMap(({ canvas }) => canvas.fields ?? [])
        .filter(field =>
          field.requirement_key ? organizationRequirementKeys.has(field.requirement_key) : false,
        )
        .map(field => [field.field_id, field]),
    ).values(),
  ];
  const firstCatalogVersion = successfulProviders[0]?.form?.catalog_version;
  const isSummaryPage = SUMMARY_PAGES.has(currentPageKey);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
          {pageLabel(currentPageKey)} — All providers
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Organization information appears once. Provider information remains grouped by provider.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Overall: {aggregate.profile_summary.percent_complete}% complete ·{' '}
          {aggregate.profile_summary.complete_count} complete ·{' '}
          {aggregate.profile_summary.missing_count} missing ·{' '}
          {aggregate.profile_summary.needs_attention_count} need attention
        </Typography>
      </Box>

      {(aggregate.omitted_provider_count ?? 0) > 0 && (
        <Alert severity="info">
          {aggregate.omitted_provider_count} rostered provider
          {aggregate.omitted_provider_count === 1 ? ' was' : 's were'} omitted because current
          active tenant membership could not be verified.
        </Alert>
      )}

      {isSummaryPage &&
        providers.map(provider =>
          provider.error ? (
            <Alert severity="warning" key={provider.user_id}>
              {provider.display_name} could not be loaded. Other providers remain available.
            </Alert>
          ) : (
            <ProviderSummary key={provider.user_id} provider={provider} />
          ),
        )}

      {!isSummaryPage && firstCatalogVersion && organizationFields.length > 0 && (
        <AggregateFieldGroup
          catalogVersion={firstCatalogVersion}
          fields={organizationFields}
          items={aggregate.organization_items ?? []}
          organizationId={organizationId}
          title="Organization"
          userId={null}
        />
      )}

      {!isSummaryPage &&
        providers.map(provider => {
          if (provider.error || !provider.form) {
            return (
              <Alert severity="warning" key={provider.user_id}>
                {provider.display_name} could not be loaded. Other providers remain available.
              </Alert>
            );
          }
          const canvas = provider.form.canvases?.find(
            entry => entry.canvas_key === currentPageKey && entry.is_active,
          );
          const fields = (canvas?.fields ?? []).filter(
            field =>
              !field.requirement_key || !organizationRequirementKeys.has(field.requirement_key),
          );
          if (DOCUMENT_PAGES.has(currentPageKey)) {
            return (
              <Card variant="outlined" key={provider.user_id}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {provider.display_name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Provider ID {provider.user_id}
                  </Typography>
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Document status is visible here, but uploads require selecting this provider to
                    preserve document ownership.
                  </Alert>
                  {fields.map(field => (
                    <Typography variant="body2" key={field.field_id} sx={{ mt: 1 }}>
                      {field.label}
                    </Typography>
                  ))}
                </CardContent>
              </Card>
            );
          }
          if (fields.length === 0) {
            return (
              <Card variant="outlined" key={provider.user_id}>
                <CardContent>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {provider.display_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No authorized fields are required for this provider in this section.
                  </Typography>
                </CardContent>
              </Card>
            );
          }
          return (
            <AggregateFieldGroup
              key={provider.user_id}
              catalogVersion={provider.form.catalog_version}
              fields={fields}
              items={provider.items ?? []}
              organizationId={organizationId}
              title={provider.display_name}
              userId={provider.user_id}
            />
          );
        })}

      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Button
          disabled={(aggregate.page ?? 1) <= 1 || isFetching}
          onClick={() => onPageChange((aggregate.page ?? 1) - 1)}
        >
          Previous provider page
        </Button>
        <Typography variant="body2" color="text.secondary">
          Provider page {aggregate.page ?? 1} of {aggregate.total_pages ?? 1}
        </Typography>
        <Button
          disabled={(aggregate.page ?? 1) >= (aggregate.total_pages ?? 1) || isFetching}
          onClick={() => onPageChange((aggregate.page ?? 1) + 1)}
        >
          Next provider page
        </Button>
      </Stack>
    </Stack>
  );
}
